import { useEffect, useState } from 'react';
import {
  composeWithinDaysValue,
  operatorNeedsValue,
  parseWithinDaysValue,
  WITHIN_DAYS_OPERATOR,
} from './articleSearchConfigs';
import type {
  ActiveFilter,
  FilterBarConfig,
  FilterBarState,
  FilterField,
  FilterFieldOption,
} from '../types';

// A within_days value is the composite `<date>,<days>`; every other date
// operator takes a bare date. When the operator changes, convert the stored
// value between the two shapes (or clear it) so it is never malformed for its
// new operator — the backend rejects e.g. a bare date under within_days.
function coerceValueForOperatorChange(prevOperator: string, nextOperator: string, value: string): string {
  if (!operatorNeedsValue(nextOperator)) return '';
  if (prevOperator === WITHIN_DAYS_OPERATOR && nextOperator !== WITHIN_DAYS_OPERATOR) {
    return parseWithinDaysValue(value).date;
  }
  if (nextOperator === WITHIN_DAYS_OPERATOR && prevOperator !== WITHIN_DAYS_OPERATOR) {
    return '';
  }
  return value;
}

function nextId(): string {
  return crypto.randomUUID();
}

function getDefaultOperator(field: FilterFieldOption): string {
  return field.defaultOperator ?? (field.type === 'text' ? 'contains' : '>=');
}

function getInputType(field: FilterFieldOption): string {
  if (field.inputType) return field.inputType;
  if (field.type === 'date') return 'date';
  if (field.type === 'number') return 'number';
  return 'text';
}

interface Props {
  state: FilterBarState;
  config: FilterBarConfig;
  onChange: (state: FilterBarState) => void;
}

export function ArticleFilterBar({ state, config, onChange }: Props) {
  const { filters, orderBy, orderDir } = state;
  const defaultState = config.defaultState;
  const firstField = config.fields[0];

  const fieldMap = new Map(config.fields.map((field) => [field.value, field]));

  const setFilters = (nextFilters: ActiveFilter[]) => onChange({ ...state, filters: nextFilters });
  const currentSortOption = config.sortOptions.find((o) => o.value === orderBy);

  const setOrder = (nextOrderBy: string, nextOrderDir: 'asc' | 'desc') => {
    const opt = config.sortOptions.find((o) => o.value === nextOrderBy);
    if (opt?.descOnly) nextOrderDir = 'desc';
    onChange({ ...state, orderBy: nextOrderBy, orderDir: nextOrderDir });
  };

  const addFilter = () => {
    setFilters([
      ...filters,
      {
        id: nextId(),
        field: firstField.value,
        operator: getDefaultOperator(firstField),
        value: '',
      },
    ]);
  };

  const removeFilter = (id: string) => setFilters(filters.filter((filter) => filter.id !== id));

  const updateFilter = (id: string, patch: Partial<Omit<ActiveFilter, 'id'>>) => {
    setFilters(filters.map((filter) => {
      if (filter.id !== id) return filter;
      const updated = { ...filter, ...patch };
      if (patch.field && patch.field !== filter.field) {
        const nextField = fieldMap.get(patch.field);
        if (!nextField) return filter;
        updated.operator = getDefaultOperator(nextField);
        updated.value = '';
      }
      if (patch.operator && patch.operator !== filter.operator) {
        updated.value = coerceValueForOperatorChange(filter.operator, patch.operator, filter.value);
      }
      return updated;
    }));
  };

  const clearAll = () => onChange(defaultState);

  const hasState =
    filters.length > 0 ||
    orderBy !== defaultState.orderBy ||
    orderDir !== defaultState.orderDir;

  return (
    <div className="article-filter-bar">
      <div className="filter-bar-top">
        {config.presets?.map((preset) => (
          <button
            key={preset.id}
            className={`filter-preset-button${preset.isActive(state) ? ' active' : ''}`}
            onClick={() => onChange(preset.apply())}
            type="button"
          >
            {preset.label}
          </button>
        ))}

        <div className="filter-sort-row">
          <span className="filter-sort-label">{config.sortLabel ?? 'Sort by'}</span>
          <select
            className="filter-sort-select"
            value={orderBy}
            onChange={(event) => setOrder(event.target.value, orderDir)}
          >
            {config.sortOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          {!currentSortOption?.descOnly && (
            <button
              className="filter-dir-toggle"
              type="button"
              onClick={() => setOrder(orderBy, orderDir === 'desc' ? 'asc' : 'desc')}
              title={orderDir === 'desc' ? 'Descending — click to flip' : 'Ascending — click to flip'}
            >
              {currentSortOption?.dirCategory === 'value'
                ? (orderDir === 'desc' ? '↓ highest first' : '↑ lowest first')
                : (orderDir === 'desc' ? '↓ newest first' : '↑ oldest first')}
            </button>
          )}
        </div>
      </div>

      {filters.length > 0 && (
        <div className="filter-rows">
          {filters.map((filter) => (
            <FilterRow
              key={filter.id}
              fields={config.fields}
              filter={filter}
              onChange={(patch) => updateFilter(filter.id, patch)}
              onRemove={() => removeFilter(filter.id)}
            />
          ))}
        </div>
      )}

      <div className="filter-bar-actions">
        <button className="filter-add-button" type="button" onClick={addFilter}>
          {config.addFilterLabel ?? '+ Add filter'}
        </button>
        {hasState && (
          <button className="filter-clear-button" type="button" onClick={clearAll}>
            {config.clearLabel ?? 'Clear all'}
          </button>
        )}
      </div>
    </div>
  );
}

interface FilterRowProps {
  fields: FilterFieldOption[];
  filter: ActiveFilter;
  onChange: (patch: Partial<Omit<ActiveFilter, 'id'>>) => void;
  onRemove: () => void;
}

function FilterRow({ fields, filter, onChange, onRemove }: FilterRowProps) {
  const field = fields.find((option) => option.value === filter.field) ?? fields[0];
  const operators = field.operators ?? [];
  const showValue = operatorNeedsValue(filter.operator);

  return (
    <div className="filter-row">
      <select
        className="filter-row-select"
        value={filter.field}
        onChange={(event) => onChange({ field: event.target.value as FilterField })}
      >
        {fields.map((option) => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </select>
      <select
        className="filter-row-select"
        value={filter.operator}
        onChange={(event) => onChange({ operator: event.target.value })}
      >
        {operators.map((operator) => (
          <option key={operator.value} value={operator.value}>{operator.label}</option>
        ))}
      </select>
      {showValue && (filter.operator === WITHIN_DAYS_OPERATOR ? (
        <WithinDaysValueInput
          value={filter.value}
          onCommit={(value) => onChange({ value })}
        />
      ) : (
        <FilterValueInput
          inputType={getInputType(field)}
          value={filter.value}
          onCommit={(value) => onChange({ value })}
          placeholder={field.placeholder ?? 'value…'}
          min={field.min}
          max={field.max}
        />
      ))}
      <button className="filter-row-delete" type="button" onClick={onRemove} title="Remove filter">
        ×
      </button>
    </div>
  );
}

interface FilterValueInputProps {
  inputType: string;
  value: string;
  onCommit: (value: string) => void;
  placeholder: string;
  min?: number;
  max?: number;
}

/**
 * Controlled filter input. Search submission is explicit, so draft values can
 * update immediately without launching a request.
 */
function FilterValueInput({ inputType, value, onCommit, placeholder, min, max }: FilterValueInputProps) {
  return (
    <input
      className="filter-row-value"
      type={inputType}
      value={value}
      onChange={(event) => onCommit(event.target.value)}
      placeholder={placeholder}
      min={min}
      max={max}
    />
  );
}

interface WithinDaysValueInputProps {
  value: string;
  onCommit: (value: string) => void;
}

/**
 * Two-part input for the date `within_days` operator, whose backend value is
 * the composite `<date>,<days>` (e.g. "2026-03-15,2"). Commits the composite
 * only once both parts are filled in; until then it commits the empty string,
 * which marks the filter row incomplete so it is skipped rather than sent to
 * the backend malformed.
 */
function WithinDaysValueInput({ value, onCommit }: WithinDaysValueInputProps) {
  const initial = parseWithinDaysValue(value);
  const [date, setDate] = useState(initial.date);
  const [days, setDays] = useState(initial.days);

  useEffect(() => {
    const next = parseWithinDaysValue(value);
    setDate(next.date);
    setDays(next.days);
  }, [value]);

  const update = (nextDate: string, nextDays: string) => {
    setDate(nextDate);
    setDays(nextDays);
    const next = composeWithinDaysValue(nextDate, nextDays);
    if (next !== value) onCommit(next);
  };

  return (
    <>
      <input
        className="filter-row-value"
        type="date"
        value={date}
        onChange={(event) => update(event.target.value, days)}
      />
      <span className="filter-row-within-days-sep">±</span>
      <input
        className="filter-row-value filter-row-within-days"
        type="number"
        value={days}
        onChange={(event) => update(date, event.target.value)}
        placeholder="days"
        title="Match dates within this many days of the chosen date"
        min={0}
      />
    </>
  );
}
